import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Quote } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY = { name: "", role: "", quote: "", image: "", active: true };

function initials(name = "") {
  return name.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null); // null | {mode, data}
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api
      .get("/admin/testimonials")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e, "Failed to load testimonials.")));
  }, []);

  useEffect(() => load(), [load]);

  const save = async () => {
    const { mode, data } = dialog;
    if (!data.name.trim() || !data.quote.trim()) {
      toast.error("Name and quote are required.");
      return;
    }
    setSaving(true);
    try {
      if (mode === "add") {
        const r = await api.post("/admin/testimonials", data);
        setItems((list) => [...list, r.data]);
      } else {
        await api.put(`/admin/testimonials/${data.id}`, data);
        setItems((list) => list.map((t) => (t.id === data.id ? data : t)));
      }
      toast.success(mode === "add" ? "Testimonial published" : "Testimonial updated");
      setDialog(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (t, active) => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, { ...t, active });
      setItems((list) => list.map((x) => (x.id === t.id ? { ...x, active } : x)));
      toast.success(active ? "Now visible on the website" : "Hidden from the website");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setItems((list) => list.filter((t) => t.id !== id));
      toast.success("Testimonial deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="testimonials-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-500">
          Publish real graduate stories — name, course, placement and photo appear on the homepage instantly.
        </p>
        <Button onClick={() => setDialog({ mode: "add", data: { ...EMPTY } })} data-testid="add-testimonial-button" className="rounded-full bg-forest font-display text-cream hover:bg-forest-deep">
          <Plus className="mr-2 h-4 w-4" /> Add Testimonial
        </Button>
      </div>

      <div className="mt-4 space-y-3" data-testid="testimonials-list">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className="flex flex-col gap-4 rounded-2xl border border-forest/10 bg-white p-5 sm:flex-row sm:items-center">
            {t.image ? (
              <img src={t.image} alt={t.name} className="h-14 w-14 shrink-0 rounded-full object-cover" data-testid={`testimonial-photo-${t.id}`} />
            ) : (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-forest/10 font-serif text-xl font-bold text-forest" data-testid={`testimonial-photo-${t.id}`}>
                {initials(t.name)}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold text-forest-deep">{t.name}</p>
              {t.role && <p className="text-xs uppercase tracking-[0.12em] text-terracotta-dark">{t.role}</p>}
              <p className="mt-1.5 line-clamp-2 text-sm italic text-stone-600">"{t.quote}"</p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-stone-500">
                <Switch checked={t.active} onCheckedChange={(v) => toggle(t, v)} data-testid={`testimonial-active-${t.id}`} />
                {t.active ? "Live" : "Hidden"}
              </label>
              <button onClick={() => setDialog({ mode: "edit", data: { ...t } })} data-testid={`testimonial-edit-${t.id}`} className="rounded-full border border-forest/20 p-2 text-forest hover:bg-forest hover:text-cream" aria-label="Edit testimonial">
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-600 hover:text-white" aria-label="Delete testimonial">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="rounded-2xl border border-dashed border-forest/20 bg-white py-14 text-center text-sm text-stone-500" data-testid="testimonials-empty">
            No testimonials yet. Add your first graduate story.
          </p>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-lg" data-testid="testimonial-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-forest-deep">
                  {dialog.mode === "add" ? "Add Graduate Story" : "Edit Graduate Story"}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Graduate Name *</label>
                  <Input
                    value={dialog.data.name}
                    onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, name: e.target.value } }))}
                    placeholder="e.g. Anitha R."
                    data-testid="testimonial-form-name"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Course & Placement</label>
                  <Input
                    value={dialog.data.role}
                    onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, role: e.target.value } }))}
                    placeholder="e.g. NTT Graduate · Placed at Little Angels School, Mandya"
                    data-testid="testimonial-form-role"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Their Words *</label>
                  <textarea
                    value={dialog.data.quote}
                    onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, quote: e.target.value } }))}
                    placeholder="What did the graduate say about their experience?"
                    data-testid="testimonial-form-quote"
                    className="mt-1.5 min-h-[110px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Photo URL (optional)</label>
                  <Input
                    value={dialog.data.image}
                    onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, image: e.target.value } }))}
                    placeholder="https://... (leave blank to show initials)"
                    data-testid="testimonial-form-image"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-stone-600">
                  <Switch
                    checked={dialog.data.active}
                    onCheckedChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, active: v } }))}
                    data-testid="testimonial-form-active"
                  />
                  Show on website
                </label>
                <Button onClick={save} disabled={saving} data-testid="testimonial-save-button" className="w-full rounded-full bg-forest py-5 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
                  {saving ? "Saving..." : "Publish Story"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
